<template>
  <component
    :is="props.to && !props.disabled ? RouterLink : 'button'"
    :to="props.to && !props.disabled ? props.to : undefined"
    :disabled="props.disabled || undefined"
    :aria-disabled="props.disabled || undefined"
    :class="['button', `${props.variant || 'text'}`, { disabled: props.disabled }, props.tone && `tone-${props.tone}`]"
    @click="handleClick"
  >
    <template v-if="props.variant === 'back'">
      <img :src="chevron" alt="Back Icon" aria-hidden="true" />
      <!-- `backLabel` overrides the i18n default when the consuming screen
           wants something other than the generic "Zurück" / "Back" — e.g.
           the results screen labels its back link "Auswahl". -->
      <span>{{ props.backLabel ?? t('back') }}</span>
    </template>
    <template v-else-if="props.variant === 'next'">
      <!-- `nextLabel` overrides the i18n default the same way `backLabel` does —
           e.g. the paywall labels its two next buttons "Jetzt kaufen" /
           "Jetzt abonnieren" instead of the generic "Weiter". -->
      <span>{{ props.nextLabel ?? t('next') }}</span>
      <img :src="chevron" alt="Next Icon" aria-hidden="true" />
    </template>
    <template v-else-if="props.variant === 'close'">
      <img :src="close" alt="Close Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('close') }}</span>
    </template>
    <template v-else-if="props.variant === 'help'">
      <img :src="help" alt="Help Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('help') }}</span>
    </template>
    <template v-else-if="props.variant === 'start'">
      <img :src="start" alt="Start Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('start') }}</span>
    </template>
    <template v-else-if="props.variant === 'settings'">
      <img :src="settings" alt="Settings Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('settings') }}</span>
    </template>
    <template v-else-if="props.variant === 'addPatient'">
      <img :src="addPatient" alt="Add Patient Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('addPatient') }}</span>
    </template>
    <template v-else-if="props.variant === 'pair'">
      <img :src="link" alt="Pair Icon" aria-hidden="true" />
      <span class="visually-hidden">{{ t('pair') }}</span>
    </template>
    <template v-else-if="props.variant === 'linked'">
      <!-- The `pair` glyph with the practice's name beside it. Same icon in
           the same corner on purpose: the glyph keeps meaning "the practice
           link", and the label says which practice it is. -->
      <span class="label">{{ props.linkedLabel }}</span>
      <span class="glyph"><img :src="link" alt="" aria-hidden="true" /></span>
    </template>
    <template v-else-if="props.variant === 'link'">
      <slot />
    </template>
    <template v-else-if="props.variant === 'largeOption'">
      <picture>
        <img :src="props.icon" alt="{{ props.title }} Icon" aria-hidden="true" />
      </picture>
      <h2>{{ props.title }}</h2>
      <span>{{ props.description }}</span>
    </template>
    <template v-else>
      <slot />
    </template>
  </component>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import chevron from '@/assets/icon/chevron.svg?no-inline'
import help from '@/assets/icon/help.svg?no-inline'
import settings from '@/assets/icon/settings.svg?no-inline'
import addPatient from '@/assets/icon/person-add.svg?no-inline'
import close from '@/assets/icon/close.svg?no-inline'
import start from '@/assets/icon/start.svg?no-inline'
import link from '@/assets/icon/link.svg?no-inline'

const props = defineProps<{
  to?: string
  variant?:
    | 'text'
    | 'link'
    | 'close'
    | 'back'
    | 'next'
    | 'help'
    | 'start'
    | 'settings'
    | 'addPatient'
    | 'pair'
    | 'linked'
    | 'largeOption'
  icon?: string
  title?: string
  description?: string
  disabled?: boolean
  /** Overrides the `back` variant's i18n-driven label. Ignored by other variants. */
  backLabel?: string
  /** Overrides the `next` variant's i18n-driven label. Ignored by other variants. */
  nextLabel?: string
  /**
   * `linked` only: the text beside the glyph, e.g. "verknüpft mit Praxis Nord".
   * Lowercase first letter on purpose: the chrome labels read as a quiet
   * status line, not a heading.
   * Passed in rather than built here because the caller owns the fallback for a
   * practice with no name. Ignored by other variants.
   */
  linkedLabel?: string
  /** largeOption only: two-tone treatment. `primary` renders the solid/dark
      card, `secondary` the lighter wash. Omitting it keeps the lighter wash. */
  tone?: 'primary' | 'secondary'
}>()

const emit = defineEmits(['click'])

function handleClick(event: Event) {
  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  if (!props.to) {
    emit('click', event)
  }
}
</script>

<style scoped lang="css">
/* Selector is `.button` (not `button`) on purpose: the root element is a
   <button> only when no `to` prop is passed; with `to`, RouterLink renders
   an <a>. Targeting the class keeps every variant styled in both shapes. */
.button {
  background: transparent;
  border: none;
  /* Anchors get a UA underline by default; strip it here so .next, .text,
     etc. render flat. The .link variant re-enables underline explicitly. */
  text-decoration: none;

  &.text {
    font-size: 1.25rem;
    font-weight: 600;

    background: rgba(0, 142, 207, 1);
    color: var(--white);
    border: 0 solid transparent;
    border-radius: 15px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);

    padding: 1.15ch 1.35ch;

    @media (hover: hover) and (pointer: fine) {
      &:not(.disabled):hover {
        background: var(--white);
        color: var(--black);
      }
    }

    &[disabled],
    &.disabled {
      background: var(--button-disabled);
      color: var(--white);
      box-shadow: none;
      /* reset.css's :disabled lives in an anonymous @layer, so its
         opacity: 0.8 still applies unless we override it — without this the
         grey washes out over light surfaces. */
      opacity: 1;
      cursor: not-allowed;
    }
  }

  &.back {
    min-height: fit-content;

    display: flex;
    align-content: center;
    align-items: center;
    column-gap: 0.75ch;

    color: var(--black);
    border: 1px solid transparent;

    text-transform: lowercase;

    padding: 0;

    img {
      width: 15%;
      height: auto;
    }

    @media (hover: hover) and (pointer: fine) {
      padding: 0.75ch 0.85ch;

      &:hover {
        border-radius: 15px;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.next {
    width: fit-content;
    min-height: fit-content;

    display: flex;
    align-content: center;
    align-items: center;
    column-gap: 0.75ch;

    font-size: 1.25rem;
    font-weight: 600;
    text-transform: lowercase;

    background: rgba(0, 142, 207, 1);
    color: var(--white);
    border: 0 solid transparent;
    border-radius: 15px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);

    margin: 0 auto;
    padding: 1.15ch 1.35ch;

    img {
      height: 15%;
      height: auto;

      transform: rotate(-180deg);

      filter: invert(100);
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: 15px;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }

    &[disabled],
    &.disabled {
      background: var(--button-disabled);
      color: var(--white);
      box-shadow: none;
      opacity: 1;
      cursor: not-allowed;

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          box-shadow: none;
        }
      }
    }
  }

  &.close {
    appearance: none;

    width: 2.75rem;
    height: 2.75rem;

    display: flex;
    justify-content: center;
    align-items: center;

    aspect-ratio: 1 / 1;

    img {
      width: 1.5rem;
      height: 1.5rem;

      aspect-ratio: 1 / 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: 5px;
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.help {
    width: 2.25rem;
    height: auto;

    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    border: 1px solid var(--black);
    border-radius: 50px;

    padding: 0.45rem;

    aspect-ratio: 1 / 1;

    img {
      width: 100%;
      height: auto;

      aspect-ratio: 1 / 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--background);
        color: var(--black);
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.start {
    width: 3.5rem;
    height: 3.5rem;

    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    background: var(--white);
    border: 2px solid var(--black);
    border-radius: 8px;

    padding: 0.75rem;

    aspect-ratio: 1 / 1;

    img {
      width: 100%;
      height: auto;

      padding-left: 0.25ch;

      aspect-ratio: 1 / 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--background);
        color: var(--black);
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.settings {
    width: 2.25rem;
    height: 100%;

    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    border: 1px solid var(--black);
    border-radius: 50px;

    padding: 0.45rem;

    aspect-ratio: 1 / 1;

    img {
      width: 100%;
      height: auto;

      aspect-ratio: 1 / 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--background);
        color: var(--black);
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  &.addPatient {
    width: 3rem;
    min-width: 3rem;
    height: 3rem;
    min-height: 3rem;

    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    border: 1.5px solid var(--black);
    border-radius: 5px;

    padding: 0.45rem;

    aspect-ratio: 1 / 1;

    img {
      width: 100%;
      height: auto;

      aspect-ratio: 1 / 1;
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: var(--background);
        color: var(--black);
        box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      }
    }
  }

  /* Blue-filled rounded square for the patient↔therapist link chrome — the
     whole of `pair`, and the glyph half of `linked`. Lives here (not in the
     consuming layout) so any future screen that needs the same chrome reuses
     the variant rather than duplicating styles, and one selector for both so
     the two can never drift: they occupy the same corner and swap places when
     the patient links or unlinks. The icon SVG already bakes stroke="white",
     so no filter inversion needed. */
  &.pair,
  &.linked span.glyph {
    width: 2.25rem;
    height: 2.25rem;
    /* `pair` is the flex item's own box; inside `linked` the glyph must not
       be squeezed by a long practice name. */
    flex: 0 0 auto;

    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;

    background: rgba(0, 142, 207, 1);
    border: 0 solid transparent;
    border-radius: 8px;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);

    padding: 0.45rem;

    aspect-ratio: 1 / 1;

    img {
      width: 100%;
      height: auto;

      aspect-ratio: 1 / 1;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &.pair:hover,
    &.linked:hover span.glyph {
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    }
  }

  &.linked {
    display: flex;
    align-items: center;
    column-gap: 0.6rem;

    cursor: pointer;

    span.label {
      font-size: 1rem;
      font-weight: 600;
      color: var(--black);
      text-align: right;

      /* Practice names are free text from the Portal. Truncate rather than
         wrap: this sits in a fixed viewport corner, and a second line would
         push the glyph out of its row and into the card below.

         The bound is what actually constrains the label — the width of the row
         it shares — not a round number. Reading right to left across the
         viewport: this button's own 1rem offset, its 2.25rem glyph and 0.6rem
         gap, then the settings cog's 2.25rem at its own 1rem offset, plus
         ~1.9rem of breathing room between the two so a long name stops short
         of the cog rather than butting against it. Anything left over is the
         label's, so it ellipsizes only when it genuinely has nowhere to go.

         Both consumers of this variant are fixed to the viewport corner, which
         is why dvw is the right unit here. */
      max-width: calc(100dvw - 9rem);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &.link {
    line-height: 140%;
    text-align: left;
    text-decoration: underline;
    text-decoration-thickness: 0.1ch;
    text-underline-offset: 0.25ch;

    color: rgba(0, 142, 207, 1);
  }

  &.largeOption {
    width: 100%;

    display: grid;
    grid-template-columns: 1fr;
    row-gap: 1rem;

    /* With a `to`, the root is an <a> (RouterLink) which would otherwise
       inherit the UA link blue; force the design's black text in both the
       anchor and <button> shapes. */
    color: var(--black);

    picture {
      display: flex;
      justify-content: center;
      align-content: center;
      align-items: center;

      /* Default (no `tone`) is the lighter wash, so existing single-tone
         choosers are unchanged. `tone-primary` overrides to the solid card. */
      background: rgba(13, 77, 128, 0.7);
      border-radius: 15px;

      margin-bottom: 0.5rem;

      aspect-ratio: 1 / 1;

      img {
        width: 40%;

        filter: invert(100);
      }
    }

    /* Title is centred under the card; the description below it keeps the
       UA-default left alignment. Matches the chooser design. */
    h2 {
      text-align: center;
    }

    &.tone-primary picture {
      background: rgba(13, 77, 128, 1);
    }

    /* Hover feedback is tone-agnostic — a lift + underline rather than a
       colour change, so the two cards keep their distinct shades instead of
       the secondary darkening into the primary on hover. Guarded to real
       pointers so it doesn't stick after a tap on touch devices. */
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        picture {
          box-shadow: 0 4px 12px rgba(13, 77, 128, 0.3);
        }

        h2 {
          text-decoration: underline;
          text-decoration-thickness: 0.15ch;
          text-underline-offset: 0.25ch;
        }
      }
    }
  }
}
</style>

<i18n>
{
  "de": {
    "back": "Zurück",
    "next": "Weiter",
    "close": "Schließen",
    "help": "Hilfe",
    "settings": "Einstellungen",
    "addPatient": "Patient erstellen",
    "pair": "Verbinden",
    "start": "Starten"
  },
  "en": {
    "back": "Back",
    "next": "Next",
    "close": "Close",
    "help": "Help",
    "settings": "Settings",
    "addPatient": "Add Patient",
    "pair": "Pair",
    "start": "Start"
  }
}
</i18n>
